import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../../../lib/api'
import { useStore } from '../../../contexts/StoreContext'
import { Order } from '../../../types'
import {
  Store,
  Package,
  Users,
  ShoppingCart,
  Clock,
  AlertCircle,
  DollarSign,
  ChevronDown,
  Building2,
  Tag,
  Settings,
  TrendingUp,
  Sparkles,
  Zap,
  BarChart3,
  ArrowRight,
  Activity
} from 'lucide-react'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import toast from 'react-hot-toast'
import { showOfflineToast } from '../../../lib/toastManager'

interface DashboardStats {
  totalProducts: number
  totalOrders: number
  totalEmployees: number
  pendingOrders: number
  todayRevenue: number
  recentOrders: Order[]
}

// Skeleton Loader Component
const StatCardSkeleton = () => (
  <div className="group bg-white/60 backdrop-blur-xl rounded-[2rem] shadow-lg border border-white/20 overflow-hidden animate-pulse">
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="w-16 h-16 bg-gray-200 rounded-xl sm:rounded-2xl"></div>
      </div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded-lg w-24"></div>
        <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
      </div>
    </div>
  </div>
)

const StoreOwnerDashboard = () => {
  const navigate = useNavigate()
  const { stores, activeStore, loading: storeLoading, setActiveStore, hasMultipleStores } = useStore()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showStoreSelector, setShowStoreSelector] = useState(false)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const lastActiveStoreIdRef = useRef<string | null>(null)
  const hasLoadedRef = useRef(false)

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640 // sm breakpoint

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  useEffect(() => {
    if (!activeStore && stores.length > 0 && !storeLoading) {
      setActiveStore(stores[0])
      return
    }
    
    if (!storeLoading && stores.length === 0) {
      setError('No tienes negocios registrados. Crea uno primero.')
      setLoading(false)
      return
    }
    
    if (activeStore) {
      const currentStoreId = activeStore.id
      
      if (lastActiveStoreIdRef.current !== currentStoreId || !hasLoadedRef.current) {
        lastActiveStoreIdRef.current = currentStoreId
        hasLoadedRef.current = true
        loadDashboardData()
      } else {
        if (!stats) {
          const cacheKey = `store_owner_dashboard_${currentStoreId}`
          const cachedStats = getCacheStale<DashboardStats>(cacheKey)
          if (cachedStats) {
            setStats(cachedStats)
            setLoading(false)
          }
        } else {
          setLoading(false)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStore?.id, stores.length, storeLoading])

  const loadDashboardData = async () => {
    if (!activeStore) return

    const cacheKey = `store_owner_dashboard_${activeStore.id}`
    const cachedStats = getCacheStale<DashboardStats>(cacheKey)
    if (cachedStats) {
      setStats(cachedStats)
      setLoading(false)
    }

    try {
      if (!cachedStats) {
        setLoading(true)
      }
      setError('')

      const [
        productsResponse,
        ordersResponse,
        employeesResponse
      ] = await Promise.all([
        api.get(`/products/store/${activeStore.id}/products`).catch(() => ({ data: { success: true, data: [] } })),
        api.get(`/orders/store/${activeStore.id}?limit=1000`).catch(() => ({ data: { success: true, data: [] } })),
        api.get(`/auth/store/${activeStore.id}/employees`).catch(() => ({ data: { success: true, data: [] } }))
      ])

      const products = productsResponse.data?.success ? (productsResponse.data.data || []) : []
      const allOrders = ordersResponse.data?.success ? (ordersResponse.data.data || []) : []
      const recentOrders = allOrders.slice(0, 5)
      const employees = employeesResponse.data?.success ? (employeesResponse.data.data || []) : []

      const today = new Date().toDateString()
      const todayOrders = allOrders.filter((order: any) =>
        new Date(order.created_at).toDateString() === today
      )

      const todayRevenue = todayOrders.reduce((sum: number, order: any) =>
        sum + (parseFloat(order.total_amount) + parseFloat(order.delivery_fee || 0)), 0
      )

      const pendingOrders = allOrders.filter((order: any) =>
        ['pending', 'confirmed', 'preparing'].includes(order.status)
      ).length

      const dashboardStats: DashboardStats = {
        totalProducts: products.length,
        totalOrders: allOrders.length,
        totalEmployees: employees.length,
        pendingOrders,
        todayRevenue,
        recentOrders: recentOrders.slice(0, 5)
      }

      setStats(dashboardStats)
      setCache(cacheKey, dashboardStats, 2 * 60 * 1000)

    } catch (err: any) {
      console.error('Error loading panel:', err)
      
      if ((err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) && cachedStats) {
        showOfflineToast()
        return
      }
      
      if (err.response?.status === 403) {
        setError('Este negocio no está activo o aprobado.')
      } else {
        setError('Error al cargar el panel. Intenta nuevamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-amber-700 bg-amber-50/80 border-amber-200/50'
      case 'preparing': return 'text-blue-700 bg-blue-50/80 border-blue-200/50'
      case 'ready': return 'text-purple-700 bg-purple-50/80 border-purple-200/50'
      case 'delivering': return 'text-orange-700 bg-orange-50/80 border-orange-200/50'
      case 'delivered': return 'text-emerald-700 bg-emerald-50/80 border-emerald-200/50'
      case 'cancelled': return 'text-red-700 bg-red-50/80 border-red-200/50'
      default: return 'text-gray-700 bg-gray-50/80 border-gray-200/50'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente'
      case 'preparing': return 'Preparando'
      case 'ready': return 'Listo'
      case 'delivering': return 'En camino'
      case 'delivered': return 'Entregado'
      case 'cancelled': return 'Cancelado'
      default: return status
    }
  }

  // Loading State
  if (storeLoading || (loading && !stats)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="mb-8">
            <div className="h-12 bg-gray-200/50 rounded-2xl w-64 mb-4 animate-pulse"></div>
            <div className="h-6 bg-gray-200/50 rounded-xl w-96 animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  // No Store State
  if (!activeStore) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-8 sm:p-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-[2rem] shadow-xl mb-8">
                <Building2 className="h-12 w-12 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">¡Bienvenido a tu panel!</h1>
              <p className="text-lg text-gray-600 mb-8">
                Aún no tienes ningún negocio registrado. Crea tu primer negocio para comenzar.
              </p>

              <div className="bg-gradient-to-br from-blue-50/80 to-indigo-50/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 mb-8 border border-blue-100/50">
                <h3 className="font-semibold text-blue-900 mb-4 text-left">Pasos para comenzar:</h3>
                <ol className="text-sm text-blue-800 space-y-3 text-left">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>Crea tu negocio con toda la información necesaria</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>Envía la solicitud de aprobación al administrador</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span>Espera la aprobación (puede tomar 1-2 días)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    <span>¡Comienza a vender y gestionar tus pedidos!</span>
                  </li>
                </ol>
              </div>

              <button
                onClick={() => navigate('/create-store')}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-5 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 active:scale-[0.98] flex items-center justify-center gap-3"
              >
                <Sparkles className="h-5 w-5" />
                Crear Mi Primer Negocio
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-[2rem] bg-red-100/80 backdrop-blur-sm mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error al cargar panel</h2>
            <p className="text-gray-600 mb-8">{error}</p>
            <button
              onClick={loadDashboardData}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 active:scale-95 font-semibold shadow-lg hover:shadow-xl"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      {/* Header - Glassmorphism Style - Ocultable en scroll en pantallas pequeñas */}
      <div className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="py-3 sm:py-6 lg:py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent truncate">
                    {activeStore.name}
                  </h1>
                  {hasMultipleStores && (
                    <div className="relative">
                      <button
                        onClick={() => setShowStoreSelector(!showStoreSelector)}
                        className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-xl sm:rounded-2xl text-xs sm:text-sm bg-white/60 backdrop-blur-md hover:bg-white/80 text-gray-700 font-medium border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
                      >
                        <Store className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Cambiar Negocio</span>
                        <ChevronDown className={`h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform duration-200 ${showStoreSelector ? 'rotate-180' : ''}`} />
                      </button>
                      {showStoreSelector && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowStoreSelector(false)}
                          ></div>
                          <div className="absolute top-full mt-2 sm:mt-3 left-0 w-full sm:w-80 bg-white/80 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl z-20 border border-white/30 overflow-hidden">
                            <div className="p-3 sm:p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 backdrop-blur-sm border-b border-white/20">
                              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Seleccionar Negocio</p>
                            </div>
                            <div className="py-2 max-h-96 overflow-y-auto">
                              {stores.map((store) => (
                                <button
                                  key={store.id}
                                  onClick={() => {
                                    setActiveStore(store)
                                    setShowStoreSelector(false)
                                  }}
                                  className={`w-full text-left px-4 sm:px-5 py-3 sm:py-4 hover:bg-white/60 transition-all duration-200 ${
                                    activeStore.id === store.id ? 'bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border-l-4 border-blue-600' : ''
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className={`font-bold text-sm sm:text-base truncate ${activeStore.id === store.id ? 'text-blue-700' : 'text-gray-900'}`}>
                                        {store.name}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1 truncate">{store.address}</div>
                                    </div>
                                    {activeStore.id === store.id && (
                                      <div className="ml-2 sm:ml-3 flex-shrink-0">
                                        <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-blue-600 rounded-full shadow-lg"></div>
                                      </div>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-gray-600 font-medium">
                  Panel de administración {hasMultipleStores && `• ${stores.length} negocios`}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className={`inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-xs font-bold border backdrop-blur-sm ${
                  activeStore.isActive
                    ? 'bg-emerald-50/80 text-emerald-700 border-emerald-200/50'
                    : 'bg-red-50/80 text-red-700 border-red-200/50'
                }`}>
                  <Activity className="h-3 w-3 mr-1 sm:mr-1.5" />
                  <span className="hidden xs:inline">{activeStore.isActive ? 'Activo' : 'Inactivo'}</span>
                  <span className="xs:hidden">{activeStore.isActive ? 'A' : 'I'}</span>
                </span>
                <span className={`inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-xs font-bold border backdrop-blur-sm ${
                  activeStore.status === 'approved'
                    ? 'bg-emerald-50/80 text-emerald-700 border-emerald-200/50'
                    : activeStore.status === 'pending'
                    ? 'bg-amber-50/80 text-amber-700 border-amber-200/50'
                    : 'bg-red-50/80 text-red-700 border-red-200/50'
                }`}>
                  {activeStore.status === 'approved' ? 'Aprobado' :
                   activeStore.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
        {/* Stats Cards - Floating Glass Cards - Compacto en móviles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-10">
          <Link
            to={`/store/${activeStore.id}/products`}
            className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 hover:bg-white/80"
          >
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Package className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                </div>
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300 hidden sm:block" />
              </div>
              <dl>
                <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-2">
                  Productos
                </dt>
                <dd className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2 sm:mb-4">
                  {stats.totalProducts}
                </dd>
              </dl>
              <div className="text-xs sm:text-sm font-semibold text-blue-600 group-hover:text-blue-700 hidden sm:block">
                Gestionar →
              </div>
            </div>
          </Link>

          <Link
            to={`/store/${activeStore.id}/orders`}
            className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 hover:bg-white/80"
          >
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                </div>
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all duration-300 hidden sm:block" />
              </div>
              <dl>
                <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-2">
                  Pedidos
                </dt>
                <dd className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2 sm:mb-4">
                  {stats.totalOrders}
                </dd>
              </dl>
              <div className="text-xs sm:text-sm font-semibold text-purple-600 group-hover:text-purple-700 hidden sm:block">
                Ver pedidos →
              </div>
            </div>
          </Link>

          <div className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 hover:bg-white/80">
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                </div>
                {stats.pendingOrders > 0 && (
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-amber-500 rounded-full animate-pulse shadow-lg"></div>
                )}
              </div>
              <dl>
                <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-2">
                  Pendientes
                </dt>
                <dd className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {stats.pendingOrders}
                </dd>
              </dl>
            </div>
          </div>

          <div className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 hover:bg-white/80">
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                </div>
                <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
              </div>
              <dl>
                <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-2">
                  Ingresos Hoy
                </dt>
                <dd className="text-lg sm:text-2xl lg:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent leading-tight">
                  RD$ {stats.todayRevenue.toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        {/* Recent Orders - Glass Card - Compacto */}
        <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-10 mb-6 sm:mb-10">
          <div className="flex items-center justify-between mb-4 sm:mb-6 lg:mb-8">
            <h3 className="text-lg sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Pedidos Recientes
            </h3>
            {stats.recentOrders.length > 0 && (
              <Link
                to={`/store/${activeStore.id}/orders`}
                className="text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 sm:gap-2 transition-colors"
              >
                <span className="hidden sm:inline">Ver todos</span>
                <span className="sm:hidden">Todos</span>
                <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Link>
            )}
          </div>
          <div className="space-y-3 sm:space-y-4">
            {stats.recentOrders.length === 0 ? (
              <div className="text-center py-8 sm:py-12 lg:py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gray-100/80 rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
                  <ShoppingCart className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 font-semibold text-sm sm:text-base lg:text-lg">No hay pedidos recientes</p>
              </div>
            ) : (
              stats.recentOrders.map((order: any) => (
                <Link
                  key={order.id}
                  to={`/store/${activeStore.id}/orders/${order.id}`}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-4 sm:p-6 bg-white/40 backdrop-blur-sm border border-white/30 rounded-xl sm:rounded-2xl hover:bg-white/60 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-1 sm:mb-2 group-hover:text-blue-600 transition-colors truncate">
                      {order.customer_name}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">
                      {new Date(order.created_at).toLocaleDateString('es-DO', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                    <span className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-xs font-bold border backdrop-blur-sm ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                    <span className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent ml-auto sm:ml-0">
                      RD$ {parseFloat(order.total_amount).toLocaleString()}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions - Grid - Compacto */}
        <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-10">
          <h3 className="text-lg sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4 sm:mb-6 lg:mb-8">
            Acciones Rápidas
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            <Link
              to={`/store/${activeStore.id}/products`}
              className="group flex flex-col items-center justify-center p-4 sm:p-5 lg:p-8 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-white bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95"
            >
              <Package className="mb-2 sm:mb-3 lg:mb-4 h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-center leading-tight">Productos</span>
            </Link>
            <Link
              to={`/store/${activeStore.id}/categories`}
              className="group flex flex-col items-center justify-center p-4 sm:p-5 lg:p-8 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 bg-white/60 backdrop-blur-sm border border-white/30 hover:bg-white/80 hover:shadow-lg sm:hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95"
            >
              <Tag className="mb-2 sm:mb-3 lg:mb-4 h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-center leading-tight">Categorías</span>
            </Link>
            <Link
              to={`/store/${activeStore.id}/orders`}
              className="group flex flex-col items-center justify-center p-4 sm:p-5 lg:p-8 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 bg-white/60 backdrop-blur-sm border border-white/30 hover:bg-white/80 hover:shadow-lg sm:hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95"
            >
              <ShoppingCart className="mb-2 sm:mb-3 lg:mb-4 h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-center leading-tight">Pedidos</span>
            </Link>
            <Link
              to={`/store/${activeStore.id}/employees`}
              className="group flex flex-col items-center justify-center p-4 sm:p-5 lg:p-8 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 bg-white/60 backdrop-blur-sm border border-white/30 hover:bg-white/80 hover:shadow-lg sm:hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95"
            >
              <Users className="mb-2 sm:mb-3 lg:mb-4 h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-center leading-tight">Empleados</span>
            </Link>
            <Link
              to={`/store/${activeStore.id}/settings`}
              className="group flex flex-col items-center justify-center p-4 sm:p-5 lg:p-8 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 bg-white/60 backdrop-blur-sm border border-white/30 hover:bg-white/80 hover:shadow-lg sm:hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95"
            >
              <Settings className="mb-2 sm:mb-3 lg:mb-4 h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-center leading-tight">Config</span>
            </Link>
            <Link
              to="/store-owner/my-stores"
              className="group flex flex-col items-center justify-center p-4 sm:p-5 lg:p-8 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 bg-white/60 backdrop-blur-sm border border-white/30 hover:bg-white/80 hover:shadow-lg sm:hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95"
            >
              <Building2 className="mb-2 sm:mb-3 lg:mb-4 h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-center leading-tight">Negocios</span>
            </Link>
            {hasMultipleStores && (
              <Link
                to="/store/dashboard"
                className="group flex flex-col items-center justify-center p-4 sm:p-5 lg:p-8 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-blue-700 bg-blue-50/80 backdrop-blur-sm border border-blue-200/50 hover:bg-blue-100/80 hover:shadow-lg sm:hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95"
              >
                <TrendingUp className="mb-2 sm:mb-3 lg:mb-4 h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 group-hover:scale-110 transition-transform duration-300" />
                <span className="text-center leading-tight">Rendimiento</span>
              </Link>
            )}
            <Link
              to={`/stores/${activeStore.slug || activeStore.id}`}
              className="group flex flex-col items-center justify-center p-4 sm:p-5 lg:p-8 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 bg-white/60 backdrop-blur-sm border border-white/30 hover:bg-white/80 hover:shadow-lg sm:hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95"
            >
              <Store className="mb-2 sm:mb-3 lg:mb-4 h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 group-hover:scale-110 transition-transform duration-300" />
              <span className="text-center leading-tight">Ver</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StoreOwnerDashboard
